import DivLoader from "components/divloader/DivLoader";
import { useRef, useState } from "react";
import toast from "react-hot-toast";
import { customerRegistrationFormFront } from "api/graphQL_customers";
import InputField from "components/fields/InputField";
import moment from "moment";
import { Select as AntSelect } from "antd";
import { MdCreditCard, MdPeople, MdVerifiedUser } from "react-icons/md";
import { BsFileText } from "react-icons/bs";
import DateInput from "components/fields/DateInput";

interface IGeneral {
  first_name: string;
  middle_name?: string;
  last_name: string;
  email: string;
  phone: string;
  city: string;
  address: string;
  postal_code: string;
  state: string;
  country: string;
  ip: string;
  birth_date?: string;
  reference_id?: string;
}

interface IKyc {
  identification_number?: string;
  identification_type?: string;
  identification_issued_at?: string;
  identification_expired_at?: string;
  document_file?: string;
  fileKey?: string;
  document_file_name?: string;
  document_file_type?: string;
}

interface ICard {
  cardholder_name: string;
  card_number: string;
  expiration_month: string;
  expiration_year: string;
  cvv: string;
}

interface IBank {
  iban: string;
  swift_no: string;
  bank_id: string;
  account_number: string;
  account_type: string;
  bank_name: string;
  bank_branch: string;
}

interface ICrypto {
  wallet_address: string;
}

interface IPaymentMethod {
  type: "bank" | "crypto" | "card";
  card?: ICard[];
  bank?: IBank[];
  crypto?: ICrypto[];
}

interface ICreateCustomerForm {
  client_id: string;
  general: IGeneral;
  kyc: IKyc[];
  payment_method: IPaymentMethod;
}

/* ── Step config ── */
const STEPS = [
  { id: 1, label: "Client",   subtitle: "Choose client",       icon: "people"   },
  { id: 2, label: "General",  subtitle: "Personal details",    icon: "person"   },
  { id: 3, label: "KYC",      subtitle: "Identity documents",  icon: "document" },
  { id: 4, label: "Payment",  subtitle: "Payment methods",     icon: "card"     },
];

const StepSVG = ({ icon, active, done }: { icon: string; active: boolean; done: boolean }) => {
  const col = done ? "#fff" : active ? "#fff" : "#94a3b8";
  const paths: Record<string, JSX.Element> = {
    people: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={col} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
      </svg>
    ),
    person: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={col} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
      </svg>
    ),
    document: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={col} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
        <polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
      </svg>
    ),
    card: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={col} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/>
      </svg>
    ),
  };
  return paths[icon] ?? null;
};

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const ErrMsg = ({ msg }: { msg?: string }) =>
  msg ? (
    <p className="crf-err">
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
      </svg>
      {msg}
    </p>
  ) : null;

function CustomerRegistrationForm({
  onClose,
  clients,
  fetchClients,
}: {
  onClose: () => void;
  clients: any;
  fetchClients: () => void;
}) {
  const rootForm: ICreateCustomerForm = {
    client_id: "",
    general: {
      first_name: "", middle_name: "", last_name: "", email: "", phone: "",
      city: "", address: "", postal_code: "", state: "", country: "", ip: "",
      birth_date: "", reference_id: "",
    },
    kyc: [{
      identification_number: "", identification_type: "",
      identification_issued_at: "", identification_expired_at: "",
      document_file: "", fileKey: Math.random().toString(36),
      document_file_name: "", document_file_type: "",
    }],
    payment_method: {
      type: "card",
      card: [{ cardholder_name: "", card_number: "", expiration_month: "", expiration_year: "", cvv: "" }],
      bank: [{ iban: "", swift_no: "", bank_id: "", account_number: "", account_type: "", bank_name: "", bank_branch: "" }],
      crypto: [{ wallet_address: "" }],
    },
  };

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [formValues, setFormValues] = useState<ICreateCustomerForm>(rootForm);
  const [formValuesErr, setFormValuesErr] = useState<any>({});
  const [step, setStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [animDir, setAnimDir] = useState<"forward" | "back">("forward");
  const [animating, setAnimating] = useState(false);
  const fileRefs = useRef<any>({});

  const totalSteps = STEPS.length;

  /* ── Validation ── */
  const validateStep1 = () => {
    const errs: any = {};
    if (!formValues.client_id) errs.client_id = "Please select a client";
    setFormValuesErr((p: any) => ({ ...p, ...errs }));
    return Object.keys(errs).length === 0;
  };

  const validateStep2 = () => {
    const optional = ["birth_date", "ip", "middle_name", "reference_id"];
    const emailRx = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const errs: any = {};
    for (const key in formValues.general) {
      const val = (formValues.general as any)[key];
      if (!val && !optional.includes(key)) { errs[key] = "The field is required"; continue; }
      if (key === "email" && val && !emailRx.test(val)) errs[key] = "Invalid email format";
    }
    setFormValuesErr((p: any) => ({ ...p, general: errs }));
    return Object.keys(errs).length === 0;
  };

  const validateStep3 = () => {
    const kycErrs: any[] = [];
    let hasErr = false;
    formValues.kyc.forEach((item, i) => {
      const e: any = {};
      const num = item.identification_number?.trim();
      const type = item.identification_type?.trim();
      const issued = item.identification_issued_at?.trim();
      const expire = item.identification_expired_at?.trim();
      if (num && !type) { e.identification_type = "The field is required"; hasErr = true; }
      if (!num && type) { e.identification_number = "The field is required"; hasErr = true; }
      if (!num && !type) { e.identification_number = "The field is required"; e.identification_type = "The field is required"; hasErr = true; }
      if (issued && !expire) { e.identification_expired_at = "The field is required"; hasErr = true; }
      if (expire && !issued) { e.identification_issued_at = "The field is required"; hasErr = true; }
      if (!item.document_file) { e.document_file = "Document is required"; hasErr = true; }
      kycErrs[i] = e;
    });
    setFormValuesErr((p: any) => ({ ...p, kyc: kycErrs }));
    return !hasErr;
  };

  const validateStep4 = () => {
    const pm = formValues.payment_method;
    const errs: any = { payment_method: { type: pm.type } };
    let hasErr = false;
    if (pm.type === "card") {
      errs.payment_method.card = pm.card?.map((c) => {
        const e: any = {};
        (["cardholder_name","card_number","expiration_month","expiration_year","cvv"] as const).forEach(f => {
          if (!c[f]?.toString().trim()) { e[f] = "The field is required"; hasErr = true; }
        });
        return e;
      });
    } else if (pm.type === "bank") {
      errs.payment_method.bank = pm.bank?.map((b) => {
        const e: any = {};
        if (!b.iban?.trim()) { e.iban = "The field is required"; hasErr = true; }
        if (!b.swift_no?.trim()) { e.swift_no = "The field is required"; hasErr = true; }
        return e;
      });
    } else if (pm.type === "crypto") {
      errs.payment_method.crypto = pm.crypto?.map((c) => {
        const e: any = {};
        if (!c.wallet_address?.trim()) { e.wallet_address = "The field is required"; hasErr = true; }
        return e;
      });
    }
    setFormValuesErr((p: any) => ({ ...p, ...errs }));
    return !hasErr;
  };

  /* ── Navigation ── */
  const animateToStep = (next: number) => {
    if (animating) return;
    setAnimDir(next > step ? "forward" : "back");
    setAnimating(true);
    setTimeout(() => { setStep(next); setAnimating(false); }, 280);
  };

  const handleNext = () => {
    let valid = false;
    if (step === 1) valid = validateStep1();
    else if (step === 2) valid = validateStep2();
    else if (step === 3) valid = validateStep3();
    if (valid) {
      setCompletedSteps((p) => new Set([...p, step]));
      animateToStep(step + 1);
    }
  };

  const handleBack = () => animateToStep(step - 1);

  const handleStepClick = (idx: number) => {
    const s = idx + 1;
    if (s < step || completedSteps.has(s)) animateToStep(s);
  };

  /* ── Submit ── */
  const handleSubmit = async (e: any) => {
    e.preventDefault();
    if (!validateStep4()) return;
    setIsLoading(true);
    const create = await customerRegistrationFormFront(formValues);
    if (create.status === "Error") {
      toast.error(create.message ?? "Error creating customer");
      setIsLoading(false);
    } else if (create.status) {
      toast.success(create.message ?? "Customer created successfully");
      onClose();
      fetchClients();
      setIsLoading(false);
    } else {
      toast.error(create.message ?? "Error creating customer");
      setIsLoading(false);
    }
  };

  /* ── KYC helpers ── */
  const addKyc = () => {
    setFormValues((p: any) => ({
      ...p,
      kyc: [...p.kyc, {
        identification_number: "", identification_type: "",
        identification_issued_at: "", identification_expired_at: "",
        document_file: "", fileKey: crypto.randomUUID(),
        document_file_name: "", document_file_type: "",
      }],
    }));
  };

  const removeKyc = (index: number) => {
    setFormValues((p: any) => ({ ...p, kyc: p.kyc.filter((_: any, i: number) => i !== index) }));
    setFormValuesErr((p: any) => {
      if (!p?.kyc) return p;
      const kyc = p.kyc.filter((_: any, i: number) => i !== index);
      return kyc.length === 0 ? (({ kyc: _, ...r }) => r)(p) : { ...p, kyc };
    });
  };

  const compressImage = (file: File, maxWidth = 1280, quality = 0.6): Promise<string> =>
    new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        let w = img.width, h = img.height;
        if (w > maxWidth) { h = (maxWidth / w) * h; w = maxWidth; }
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d")!.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.src = URL.createObjectURL(file);
    });

  const handleFileUpload = async (e: any, fileKey: string, index: number) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Max file size is 2 MB"); return; }
    let base64 = "", fileType = "";
    if (file.type.startsWith("image/")) {
      base64 = await compressImage(file); fileType = "image";
    } else if (file.type === "application/pdf") {
      fileType = "pdf";
      const reader = new FileReader();
      base64 = await new Promise(r => { reader.onloadend = () => r(reader.result as string); reader.readAsDataURL(file); });
    } else { toast.error("Only image and PDF files are allowed"); return; }
    setFormValues((p: any) => {
      const kyc = [...p.kyc];
      kyc[index] = { ...kyc[index], document_file: base64, document_file_type: fileType, document_file_name: file.name };
      return { ...p, kyc };
    });
    setFormValuesErr((p: any) => {
      const u = { ...p };
      if (!u.kyc) u.kyc = [];
      if (!u.kyc[index]) u.kyc[index] = {};
      delete u.kyc[index].document_file;
      return u;
    });
  };

  const progressPct = ((step - 1) / (totalSteps - 1)) * 100;

  /* ── Step content ── */
  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="crf-step-body">
            <div className="crf-step-intro">
              <div className="crf-intro-icon crf-intro-icon--blue">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
              </div>
              <div>
                <h3 className="crf-step-heading">Choose Client</h3>
                <p className="crf-step-desc">Select the client this customer belongs to.</p>
              </div>
            </div>
            <div className="crf-field-wrap">
              <label className="crf-label">Client <span className="crf-req">*</span></label>
              <AntSelect
                showSearch
                filterOption={(input, option) => String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
                allowClear
                placeholder="Search and select client…"
                className="crf-ant-select"
                options={clients?.map((m: any) => ({ label: m?.name, value: m?.id }))}
                value={formValues?.client_id || undefined}
                onChange={(e: any) => {
                  setFormValues((p: any) => ({ ...p, client_id: e }));
                  setFormValuesErr((p: any) => ({ ...p, client_id: "" }));
                }}
                notFoundContent="No clients found"
              />
              {formValuesErr?.client_id && (
                <ErrMsg msg={formValuesErr?.client_id} />
              )}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="crf-step-body">
            <div className="crf-step-intro">
              <div className="crf-intro-icon crf-intro-icon--purple">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                </svg>
              </div>
              <div>
                <h3 className="crf-step-heading">General Information</h3>
                <p className="crf-step-desc">Enter the customer's personal details.</p>
              </div>
            </div>
            <div className="crf-grid">
              {/* First / Middle / Last name */}
              <div className="crf-field-wrap">
                <label className="crf-label">First Name <span className="crf-req">*</span></label>
                <input className={`crf-input${formValuesErr?.general?.first_name ? " crf-input--err" : ""}`} placeholder="John" value={formValues.general.first_name}
                  onChange={e => { const v = e.target.value; setFormValues((p:any) => ({...p, general:{...p.general, first_name:v}})); setFormValuesErr((p:any) => ({...p, general:{...p.general, first_name:""}})); }} />
                {formValuesErr?.general?.first_name && (
                    <ErrMsg msg={formValuesErr?.general?.first_name} />
                )}
              </div>
              <div className="crf-field-wrap">
                <label className="crf-label">Middle Name <span className="crf-opt">(optional)</span></label>
                <input className="crf-input" placeholder="M." value={formValues.general.middle_name ?? ""}
                  onChange={e => setFormValues((p:any) => ({...p, general:{...p.general, middle_name:e.target.value}}))} />
              </div>
              <div className="crf-field-wrap">
                <label className="crf-label">Last Name <span className="crf-req">*</span></label>
                <input className={`crf-input${formValuesErr?.general?.last_name ? " crf-input--err" : ""}`} placeholder="Doe" value={formValues.general.last_name}
                  onChange={e => { const v = e.target.value; setFormValues((p:any) => ({...p, general:{...p.general, last_name:v}})); setFormValuesErr((p:any) => ({...p, general:{...p.general, last_name:""}})); }} />
                {formValuesErr?.general?.last_name && (
                    <ErrMsg msg={formValuesErr?.general?.last_name} />
                )}
              </div>
              <div className="crf-field-wrap">
                <label className="crf-label">Email <span className="crf-req">*</span></label>
                <input className={`crf-input${formValuesErr?.general?.email ? " crf-input--err" : ""}`} placeholder="john@example.com" value={formValues.general.email}
                  onChange={e => { const v = e.target.value; setFormValues((p:any) => ({...p, general:{...p.general, email:v}})); setFormValuesErr((p:any) => ({...p, general:{...p.general, email:""}})); }} />
                {formValuesErr?.general?.email && (
                <ErrMsg msg={formValuesErr?.general?.email} />
                )}
              </div>
              <div className="crf-field-wrap">
                <label className="crf-label">Phone <span className="crf-req">*</span></label>
                <input className={`crf-input${formValuesErr?.general?.phone ? " crf-input--err" : ""}`} placeholder="+1234567890" value={formValues.general.phone}
                  onChange={e => { const v = e.target.value; if(!/^\d*$/.test(v)) return; setFormValues((p:any) => ({...p, general:{...p.general, phone:v}})); setFormValuesErr((p:any) => ({...p, general:{...p.general, phone:""}})); }} />
                {formValuesErr?.general?.phone && (
                    <ErrMsg msg={formValuesErr?.general?.phone} />
                )}
              </div>
              <div className="crf-field-wrap">
                <label className="crf-label">Birth Date <span className="crf-opt">(optional)</span></label>
                <input type="date" className="crf-input" max={moment().format("YYYY-MM-DD")}
                  value={formValues.general.birth_date ? moment.utc(formValues.general.birth_date).format("YYYY-MM-DD") : ""}
                  onKeyDown={e => e.preventDefault()} onFocus={e => e.target.blur()}
                  onChange={e => { const v = e.target.value ? moment.utc(e.target.value).format("YYYY-MM-DD") : ""; setFormValues((p:any) => ({...p, general:{...p.general, birth_date:v}})); }} />
              </div>
              <div className="crf-field-wrap crf-field-full">
                <label className="crf-label">Address <span className="crf-req">*</span></label>
                <input className={`crf-input${formValuesErr?.general?.address ? " crf-input--err" : ""}`} placeholder="45 Test Street" value={formValues.general.address}
                  onChange={e => { const v = e.target.value; setFormValues((p:any) => ({...p, general:{...p.general, address:v}})); setFormValuesErr((p:any) => ({...p, general:{...p.general, address:""}})); }} />
                {formValuesErr?.general?.address && (
                    <ErrMsg msg={formValuesErr?.general?.address} />
                )}
              </div>
              <div className="crf-field-wrap">
                <label className="crf-label">City <span className="crf-req">*</span></label>
                <input className={`crf-input${formValuesErr?.general?.city ? " crf-input--err" : ""}`} placeholder="New York" value={formValues.general.city}
                  onChange={e => { const v = e.target.value; setFormValues((p:any) => ({...p, general:{...p.general, city:v}})); setFormValuesErr((p:any) => ({...p, general:{...p.general, city:""}})); }} />
                {formValuesErr?.general?.city && (
                    <ErrMsg msg={formValuesErr?.general?.city} />
                )}
              </div>
              <div className="crf-field-wrap">
                <label className="crf-label">State <span className="crf-req">*</span></label>
                <input className={`crf-input${formValuesErr?.general?.state ? " crf-input--err" : ""}`} placeholder="NY" value={formValues.general.state}
                  onChange={e => { const v = e.target.value; setFormValues((p:any) => ({...p, general:{...p.general, state:v}})); setFormValuesErr((p:any) => ({...p, general:{...p.general, state:""}})); }} />
                {formValuesErr?.general?.state && (
                    <ErrMsg msg={formValuesErr?.general?.state} />
                )}
              </div>
              <div className="crf-field-wrap">
                <label className="crf-label">Country <span className="crf-req">*</span></label>
                <input className={`crf-input${formValuesErr?.general?.country ? " crf-input--err" : ""}`} placeholder="USA" value={formValues.general.country}
                  onChange={e => { const v = e.target.value; setFormValues((p:any) => ({...p, general:{...p.general, country:v}})); setFormValuesErr((p:any) => ({...p, general:{...p.general, country:""}})); }} />
                {formValuesErr?.general?.country && (
                    <ErrMsg msg={formValuesErr?.general?.country} />
                )}
              </div>
              <div className="crf-field-wrap">
                <label className="crf-label">Postal Code <span className="crf-req">*</span></label>
                <input className={`crf-input${formValuesErr?.general?.postal_code ? " crf-input--err" : ""}`} placeholder="10001" value={formValues.general.postal_code}
                  onChange={e => { const v = e.target.value; setFormValues((p:any) => ({...p, general:{...p.general, postal_code:v}})); setFormValuesErr((p:any) => ({...p, general:{...p.general, postal_code:""}})); }} />
                {formValuesErr?.general?.postal_code && (
                    <ErrMsg msg={formValuesErr?.general?.postal_code} />
                )}
              </div>
              <div className="crf-field-wrap">
                <label className="crf-label">Reference ID <span className="crf-opt">(optional)</span></label>
                <input className="crf-input" placeholder="REF-001" value={formValues.general.reference_id ?? ""}
                  onChange={e => setFormValues((p:any) => ({...p, general:{...p.general, reference_id:e.target.value}}))} />
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="crf-step-body">
            <div className="crf-step-intro">
              <div className="crf-intro-icon crf-intro-icon--amber">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
                </svg>
              </div>
              <div>
                <h3 className="crf-step-heading">KYC Information</h3>
                <p className="crf-step-desc">Upload identity documents for verification.</p>
              </div>
            </div>

            {formValues.kyc.map((item: any, index: number) => (
              <div key={item.fileKey} className="crf-kyc-card">
                <div className="crf-kyc-card-header">
                  <span className="crf-kyc-badge">KYC #{index + 1}</span>
                  {formValues.kyc.length > 1 && (
                    <button className="crf-kyc-remove" onClick={() => removeKyc(index)}>Remove</button>
                  )}
                </div>

                <div className="crf-grid">
                  <div className="crf-field-wrap">
                    <label className="crf-label">Identification Number <span className="crf-req">*</span></label>
                    <input
                      className={`crf-input${formValuesErr?.kyc?.[index]?.identification_number ? " crf-input--err" : ""}`}
                      placeholder="ID123456"
                      value={item.identification_number}
                      onChange={e => {
                        const v = e.target.value;
                        setFormValues((p: any) => { const kyc = [...p.kyc]; kyc[index].identification_number = v; return {...p, kyc}; });
                        setFormValuesErr((p: any) => { const u = {...p}; if (!u.kyc) u.kyc = []; if (!u.kyc[index]) u.kyc[index] = {}; delete u.kyc[index].identification_number; return u; });
                      }}
                    />
                    {formValuesErr?.kyc?.[index]?.identification_number && (
                        <ErrMsg msg={formValuesErr?.kyc?.[index]?.identification_number} />
                    )}
                  </div>
                  <div className="crf-field-wrap">
                    <label className="crf-label">Identification Type <span className="crf-req">*</span></label>
                    <input
                      className={`crf-input${formValuesErr?.kyc?.[index]?.identification_type ? " crf-input--err" : ""}`}
                      placeholder="PASSPORT"
                      value={item.identification_type}
                      onChange={e => {
                        const v = e.target.value;
                        setFormValues((p: any) => { const kyc = [...p.kyc]; kyc[index].identification_type = v; return {...p, kyc}; });
                        setFormValuesErr((p: any) => { const u = {...p}; if (!u.kyc) u.kyc = []; if (!u.kyc[index]) u.kyc[index] = {}; delete u.kyc[index].identification_type; return u; });
                      }}
                    />
                    {formValuesErr?.kyc?.[index]?.identification_type && (
                        <ErrMsg msg={formValuesErr?.kyc?.[index]?.identification_type} />
                    )}
                  </div>
                  <div className="crf-field-wrap">
                    <label className="crf-label">
                      Issued At <span className="crf-opt">(optional)</span>
                    </label>
                    <DateInput
                      maxDate={new Date()}
                      value={item.identification_issued_at
                        ? moment.utc(item.identification_issued_at).format("YYYY-MM-DD")
                        : ""}
                      placeholder="dd-mm-yyyy"
                      onChange={(v:any) => {
                        setFormValues((p: any) => {
                          const kyc = [...p.kyc];
                          kyc[index].identification_issued_at = v;
                          if (!v) kyc[index].identification_expired_at = "";
                          return { ...p, kyc };
                        });
                        setFormValuesErr((p: any) => {
                          const u = { ...p };
                          if (!u.kyc) u.kyc = [];
                          if (!u.kyc[index]) u.kyc[index] = {};
                          delete u.kyc[index].identification_issued_at;
                          delete u.kyc[index].identification_expired_at;
                          return u;
                        });
                      }}
                    />
                    {formValuesErr?.kyc?.[index]?.identification_issued_at && (
                      <ErrMsg msg={formValuesErr?.kyc?.[index]?.identification_issued_at} />
                    )}
                  </div>
                  
                  {/* Expired At */}
                  <div className="crf-field-wrap">
                    <label className="crf-label">
                      Expired At <span className="crf-opt">(optional)</span>
                    </label>
                    <DateInput
                      value={item.identification_expired_at
                        ? moment.utc(item.identification_expired_at).format("YYYY-MM-DD")
                        : ""}
                      placeholder="dd-mm-yyyy"
                      disabled={!item.identification_issued_at}
                      minDate={
                        item.identification_issued_at
                          ? moment.utc(item.identification_issued_at).add(1, "day").toDate()
                          : undefined
                      }
                      onChange={(v:any) => {
                        setFormValues((p: any) => {
                          const kyc = [...p.kyc];
                          kyc[index].identification_expired_at = v;
                          return { ...p, kyc };
                        });
                        setFormValuesErr((p: any) => {
                          const u = { ...p };
                          if (!u.kyc) u.kyc = [];
                          if (!u.kyc[index]) u.kyc[index] = {};
                          if (!v && item.identification_issued_at)
                            u.kyc[index].identification_expired_at = "The field is required";
                          else
                            delete u.kyc[index].identification_expired_at;
                          return u;
                        });
                      }}
                    />
                    {formValuesErr?.kyc?.[index]?.identification_expired_at && (
                      <ErrMsg msg={formValuesErr?.kyc?.[index]?.identification_expired_at} />
                    )}
                  </div>

                  {/* Document upload */}
                  <div className="crf-field-wrap crf-field-full">
                    <label className="crf-label">Document File <span className="crf-req">*</span></label>
                    <input
                      ref={el => (fileRefs.current[item.fileKey] = el)}
                      type="file" accept="image/*,.pdf" className="crf-file-hidden"
                      onChange={e => handleFileUpload(e, item.fileKey, index)}
                    />
                    <div
                      className={`crf-dropzone${formValuesErr?.kyc?.[index]?.document_file ? " crf-dropzone--err" : ""}`}
                      onClick={() => fileRefs.current[item.fileKey]?.click()}
                    >
                      {!item.document_file && (
                        <div className="crf-dropzone-empty">
                          <div className="crf-dropzone-icon">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                              <polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
                            </svg>
                          </div>
                          <p className="crf-dropzone-title">Click to upload</p>
                          <p className="crf-dropzone-sub">PDF or Image · max 2 MB</p>
                        </div>
                      )}
                      {item.document_file && item.document_file_type === "image" && (
                        <img src={item.document_file} className="crf-dropzone-preview-img" alt="Preview" />
                      )}
                      {item.document_file && item.document_file_type === "pdf" && (
                        <div className="crf-dropzone-pdf">
                          <div className="crf-dropzone-pdf-icon">
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                              <polyline points="14 2 14 8 20 8"/>
                            </svg>
                          </div>
                          <p className="crf-dropzone-pdf-name">{item.document_file_name}</p>
                          <p className="crf-dropzone-pdf-sub">Click to replace</p>
                        </div>
                      )}
                    </div>
                    {formValuesErr?.kyc?.[index]?.document_file && (
                        <ErrMsg msg={formValuesErr?.kyc?.[index]?.document_file} />
                    )}
                  </div>
                </div>
              </div>
            ))}

            <button className="crf-add-more-btn" onClick={addKyc}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/>
              </svg>
              Add Another KYC
            </button>
          </div>
        );

      case 4:
        return (
          <div className="crf-step-body">
            <div className="crf-step-intro">
              <div className="crf-intro-icon crf-intro-icon--green">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/>
                </svg>
              </div>
              <div>
                <h3 className="crf-step-heading">Payment Methods</h3>
                <p className="crf-step-desc">Add a payment method for this customer.</p>
              </div>
            </div>

            {/* Type selector */}
            <div className="crf-pm-type-row">
              {(["card", "bank", "crypto"] as const).map(t => (
                <label key={t} className={`crf-pm-type-btn${formValues.payment_method.type === t ? " crf-pm-type-btn--active" : ""}`}>
                  <input type="radio" name="pm_type" value={t} checked={formValues.payment_method.type === t}
                    onChange={e => setFormValues((p: any) => ({...p, payment_method:{...p.payment_method, type:e.target.value}}))}
                    className="crf-pm-radio"
                  />
                  <span className="crf-pm-type-label">{t.charAt(0).toUpperCase() + t.slice(1)}</span>
                </label>
              ))}
            </div>

            {/* Card */}
            {formValues.payment_method.type === "card" && (
              <>
                {formValues.payment_method.card?.map((cardItem: ICard, i: number) => (
                  <div key={i} className="crf-pm-card">
                    <div className="crf-pm-card-header">
                      <span className="crf-pm-card-badge">Card #{i + 1}</span>
                      {(formValues.payment_method.card?.length ?? 0) > 1 && (
                        <button className="crf-kyc-remove" onClick={() => {
                          setFormValues((p: any) => ({...p, payment_method:{...p.payment_method, card:p.payment_method.card.filter((_: any, idx: number) => idx !== i)}}));
                        }}>Remove</button>
                      )}
                    </div>
                    <div className="crf-grid">
                      {[
                        { field: "cardholder_name", label: "Cardholder Name", ph: "John Doe" },
                        { field: "card_number",     label: "Card Number",     ph: "4111111111111111" },
                        { field: "expiration_month",label: "Expiry Month",    ph: "MM" },
                        { field: "expiration_year", label: "Expiry Year",     ph: "YYYY" },
                        { field: "cvv",             label: "CVV",             ph: "123" },
                      ].map(({ field, label, ph }) => (
                        <div key={field} className="crf-field-wrap">
                          <label className="crf-label">{label} <span className="crf-req">*</span></label>
                          <input
                            className={`crf-input${formValuesErr?.payment_method?.card?.[i]?.[field] ? " crf-input--err" : ""}`}
                            placeholder={ph}
                            value={(cardItem as any)[field]}
                            onChange={e => {
                              const v = e.target.value;
                              if ((field === "card_number" || field === "expiration_month" || field === "expiration_year" || field === "cvv") && !/^\d*$/.test(v)) return;
                              if (field === "expiration_month" && v.length > 2) return;
                              if (field === "expiration_year" && v.length > 4) return;
                              if (field === "cvv" && v.length > 3) return;
                              setFormValues((p: any) => { const card = [...p.payment_method.card]; card[i] = {...card[i], [field]:v}; return {...p, payment_method:{...p.payment_method, card}}; });
                              setFormValuesErr((p: any) => { const u = {...p}; if (!u.payment_method?.card) return u; if (!u.payment_method.card[i]) u.payment_method.card[i] = {}; delete u.payment_method.card[i][field]; return u; });
                            }}
                          />
                          {formValuesErr?.payment_method?.card?.[i]?.[field] && (
                            <ErrMsg msg={formValuesErr?.payment_method?.card?.[i]?.[field]} />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                <button className="crf-add-more-btn" onClick={() => setFormValues((p: any) => ({...p, payment_method:{...p.payment_method, card:[...p.payment_method.card, {cardholder_name:"",card_number:"",expiration_month:"",expiration_year:"",cvv:""}]}}))}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
                  Add Another Card
                </button>
              </>
            )}

            {/* Bank */}
            {formValues.payment_method.type === "bank" && (
              <>
                {formValues.payment_method.bank?.map((bankItem: IBank, i: number) => (
                  <div key={i} className="crf-pm-card">
                    <div className="crf-pm-card-header">
                      <span className="crf-pm-card-badge">Bank #{i + 1}</span>
                      {(formValues.payment_method.bank?.length ?? 0) > 1 && (
                        <button className="crf-kyc-remove" onClick={() => { setFormValues((p: any) => ({...p, payment_method:{...p.payment_method, bank:p.payment_method.bank.filter((_: any, idx: number) => idx !== i)}})); }}>Remove</button>
                      )}
                    </div>
                    <div className="crf-grid">
                      {[
                        { field:"iban",           label:"IBAN",           ph:"GB29NWBK60161331926819", req:true  },
                        { field:"swift_no",       label:"Swift No",       ph:"NWBKGB2L",               req:true  },
                        { field:"bank_id",        label:"Bank ID",        ph:"",                       req:false },
                        { field:"account_number", label:"Account Number", ph:"",                       req:false },
                        { field:"account_type",   label:"Account Type",   ph:"CHECKING",               req:false },
                        { field:"bank_name",      label:"Bank Name",      ph:"",                       req:false },
                        { field:"bank_branch",    label:"Bank Branch",    ph:"",                       req:false },
                      ].map(({ field, label, ph, req }) => (
                        <div key={field} className="crf-field-wrap">
                          <label className="crf-label">{label} {req ? <span className="crf-req">*</span> : <span className="crf-opt">(optional)</span>}</label>
                          <input
                            className={`crf-input${formValuesErr?.payment_method?.bank?.[i]?.[field] ? " crf-input--err" : ""}`}
                            placeholder={ph}
                            value={(bankItem as any)[field]}
                            onChange={e => { const v = e.target.value; setFormValues((p: any) => { const bank = [...p.payment_method.bank]; bank[i] = {...bank[i], [field]:v}; return {...p, payment_method:{...p.payment_method, bank}}; }); if (req) setFormValuesErr((p: any) => { const u = {...p}; if (!u.payment_method?.bank) return u; if (!u.payment_method.bank[i]) u.payment_method.bank[i] = {}; delete u.payment_method.bank[i][field]; return u; }); }}
                          />
                          {formValuesErr?.payment_method?.bank?.[i]?.[field] && (
                            <ErrMsg msg={formValuesErr?.payment_method?.bank?.[i]?.[field]} />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                <button className="crf-add-more-btn" onClick={() => setFormValues((p: any) => ({...p, payment_method:{...p.payment_method, bank:[...p.payment_method.bank, {iban:"",swift_no:"",bank_id:"",account_number:"",account_type:"",bank_name:"",bank_branch:""}]}}))}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
                  Add Another Bank
                </button>
              </>
            )}

            {/* Crypto */}
            {formValues.payment_method.type === "crypto" && (
              <>
                {formValues.payment_method.crypto?.map((cryptoItem: ICrypto, i: number) => (
                  <div key={i} className="crf-pm-card">
                    <div className="crf-pm-card-header">
                      <span className="crf-pm-card-badge">Wallet #{i + 1}</span>
                      {(formValues.payment_method.crypto?.length ?? 0) > 1 && (
                        <button className="crf-kyc-remove" onClick={() => { setFormValues((p: any) => ({...p, payment_method:{...p.payment_method, crypto:p.payment_method.crypto.filter((_: any, idx: number) => idx !== i)}})); }}>Remove</button>
                      )}
                    </div>
                    <div className="crf-grid">
                      <div className="crf-field-wrap crf-field-full">
                        <label className="crf-label">Wallet Address <span className="crf-req">*</span></label>
                        <input
                          className={`crf-input${formValuesErr?.payment_method?.crypto?.[i]?.wallet_address ? " crf-input--err" : ""}`}
                          placeholder="0x1234…"
                          value={cryptoItem.wallet_address}
                          onChange={e => { const v = e.target.value; setFormValues((p: any) => { const crypto = [...p.payment_method.crypto]; crypto[i].wallet_address = v; return {...p, payment_method:{...p.payment_method, crypto}}; }); setFormValuesErr((p: any) => { const u = {...p}; if (!u.payment_method?.crypto) return u; if (!u.payment_method.crypto[i]) u.payment_method.crypto[i] = {}; delete u.payment_method.crypto[i].wallet_address; return u; }); }}
                        />
                        {formValuesErr?.payment_method?.crypto?.[i]?.wallet_address && (
                            <ErrMsg msg={formValuesErr?.payment_method?.crypto?.[i]?.wallet_address} />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                <button className="crf-add-more-btn" onClick={() => setFormValues((p: any) => ({...p, payment_method:{...p.payment_method, crypto:[...p.payment_method.crypto, {wallet_address:""}]}}))}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
                  Add Another Wallet
                </button>
              </>
            )}
          </div>
        );

      default: return null;
    }
  };

  return (
    <>
      {/* Progress bar */}
      <div className="crf-progress-track">
        <div className="crf-progress-fill" style={{ width: `${progressPct}%` }} />
      </div>

      {/* Step indicators */}
      <div className="crf-steps-row">
        {STEPS.map((s, i) => {
          const isActive = s.id === step;
          const isDone = completedSteps.has(s.id);
          const isReach = s.id < step || completedSteps.has(s.id);
          return (
            <div key={s.id} className="crf-step-item" onClick={() => isReach && handleStepClick(i)}>
              <div className={`crf-step-circle${isActive ? " crf-step-circle--active" : isDone ? " crf-step-circle--done" : " crf-step-circle--idle"}`}>
                {isDone && !isActive ? <CheckIcon /> : <StepSVG icon={s.icon} active={isActive} done={isDone} />}
                {isActive && <div className="crf-step-pulse" />}
              </div>
              <div className="crf-step-label-block">
                <span className={`crf-step-label${isActive ? " crf-step-label--active" : isDone ? " crf-step-label--done" : ""}`}>{s.label}</span>
                <span className="crf-step-sublabel">{s.subtitle}</span>
              </div>
              {i < STEPS.length - 1 && (
                <div className={`crf-step-connector${isDone ? " crf-step-connector--done" : ""}`} />
              )}
            </div>
          );
        })}
      </div>

      {/* Content */}
      <div className="crf-content">
        <div className={`crf-anim-wrap${animating ? ` crf-anim--exit-${animDir}` : ` crf-anim--enter-${animDir}`}`}>
          {renderStep()}
        </div>
      </div>

      {/* Footer */}
      <div className="crf-footer">
        <div className="crf-footer-left">
          <div className="crf-dot-row">
            {STEPS.map((s) => (
              <div key={s.id} className={`crf-dot${s.id === step ? " crf-dot--active" : completedSteps.has(s.id) ? " crf-dot--done" : ""}`} />
            ))}
          </div>
          <span className="crf-footer-hint">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
            Encrypted &amp; secure
          </span>
        </div>
        <div className="crf-footer-btns">
          {step > 1 && (
            <button className="crf-btn-back" onClick={handleBack}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
              Back
            </button>
          )}
          <button className="crf-btn-cancel" onClick={onClose}>Cancel</button>
          {step === totalSteps ? (
            <button className="crf-btn-submit" onClick={handleSubmit} disabled={isLoading}>
              {isLoading ? <span className="crf-spinner" /> : (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  Create Customer
                </>
              )}
            </button>
          ) : (
            <button className="crf-btn-next" onClick={handleNext}>
              Continue
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
            </button>
          )}
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        /* Progress */
        .crf-progress-track { height: 4px; background: #e8edf3; flex-shrink: 0; }
        .crf-progress-fill { height: 100%; background: linear-gradient(90deg,#15803d,#16a34a); border-radius: 0 4px 4px 0; transition: width .4s cubic-bezier(.4,0,.2,1); }

        /* Steps row */
        .crf-steps-row { display: flex; align-items: flex-start; padding: 16px 24px 12px; background: #fafbfc; border-bottom: 1px solid #f1f5f9; overflow-x: auto; gap: 0; }
        .crf-step-item { display: flex; align-items: center; flex: 1; min-width: 0; cursor: default; }
        .crf-step-circle { width: 36px; height: 36px; border-radius: 50%; display: flex; align-items: center; justify-content: center; flex-shrink: 0; position: relative; transition: all .25s; }
        .crf-step-circle--idle   { background: #f1f5f9; border: 2px solid #e2e8f0; }
        .crf-step-circle--active { background: linear-gradient(135deg,#15803d,#16a34a); border: 2px solid transparent; box-shadow: 0 0 0 4px rgba(22,163,74,.15); }
        .crf-step-circle--done   { background: linear-gradient(135deg,#1d4ed8,#2563eb); border: 2px solid transparent; box-shadow: 0 0 0 3px rgba(37,99,235,.12); }
        .crf-step-pulse { position: absolute; inset: -4px; border-radius: 50%; border: 2px solid rgba(22,163,74,.35); animation: crfPulse 1.8s ease-in-out infinite; }
        @keyframes crfPulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0;transform:scale(1.4)} }
        .crf-step-label-block { display: flex; flex-direction: column; gap: 1px; padding: 0 6px; min-width: 0; }
        .crf-step-label { font-size: 11.5px; font-weight: 600; color: #94a3b8; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .crf-step-label--active { color: #16a34a; font-weight: 800; }
        .crf-step-label--done   { color: #2563eb; font-weight: 700; }
        .crf-step-sublabel { font-size: 10px; color: #cbd5e1; font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .crf-step-connector { flex: 1; height: 2px; background: #e2e8f0; margin: 0 6px; border-radius: 2px; transition: background .3s; }
        .crf-step-connector--done { background: linear-gradient(90deg,#2563eb,#60a5fa); }

        /* Content */
        .crf-content { flex: 1; overflow-y: auto; overflow-x: hidden;position: relative; }
        .crf-content::-webkit-scrollbar { width: 4px; }
        .crf-content::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
        .crf-anim-wrap { }
        .crf-anim--enter-forward { animation: crfSlideInRight .28s cubic-bezier(0.16,1,0.3,1) both; }
        .crf-anim--enter-back    { animation: crfSlideInLeft  .28s cubic-bezier(0.16,1,0.3,1) both; }
        .crf-anim--exit-forward  { animation: crfSlideOutLeft .18s ease both; }
        .crf-anim--exit-back     { animation: crfSlideOutRight .18s ease both; }
        @keyframes crfSlideInRight  { from{opacity:0;transform:translateX(32px)}  to{opacity:1;transform:translateX(0)} }
        @keyframes crfSlideInLeft   { from{opacity:0;transform:translateX(-32px)} to{opacity:1;transform:translateX(0)} }
        @keyframes crfSlideOutLeft  { from{opacity:1;transform:translateX(0)} to{opacity:0;transform:translateX(-24px)} }
        @keyframes crfSlideOutRight { from{opacity:1;transform:translateX(0)} to{opacity:0;transform:translateX(24px)} }

        /* Step body */
        .crf-step-body { padding: 24px 28px 8px; font-family: 'Plus Jakarta Sans', sans-serif; }
        .crf-step-intro { display: flex; align-items: center; gap: 14px; margin-bottom: 24px; }
        .crf-intro-icon { width: 52px; height: 52px; border-radius: 14px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .crf-intro-icon--blue   { background: linear-gradient(135deg,#eff6ff,#dbeafe); border: 1.5px solid #bfdbfe; color: #2563eb; }
        .crf-intro-icon--purple { background: linear-gradient(135deg,#faf5ff,#ede9fe); border: 1.5px solid #ddd6fe; color: #7c3aed; }
        .crf-intro-icon--amber  { background: linear-gradient(135deg,#fffbeb,#fef3c7); border: 1.5px solid #fde68a; color: #d97706; }
        .crf-intro-icon--green  { background: linear-gradient(135deg,#f0fdf4,#dcfce7); border: 1.5px solid #bbf7d0; color: #16a34a; }
        .crf-step-heading { font-size: 18px; font-weight: 800; color: #0f172a; letter-spacing: -.3px; margin-bottom: 3px; line-height: 22px; }
        .crf-step-desc { font-size: 13px; color: #64748b; font-weight: 500; line-height: 1.5; }

        /* Grid */
        .crf-grid { display: grid; grid-template-columns: 1fr 1fr; column-gap: 16px; row-gap: 8px; align-items: start; }
        .crf-field-full { grid-column: 1/-1; }
        .crf-field-wrap { display: flex; flex-direction: column; }

        /* Labels / inputs */
        .crf-label { font-size: 12.5px; font-weight: 700; color: #374151; margin-bottom: 6px; display: block; }
        .crf-req   { color: #dc2626; }
        .crf-opt   { font-size: 11.5px; font-weight: 500; color: #94a3b8; }
        .crf-input {
          width: 100%; padding: 12px 14px;
          background: #f8fafc; border: 1.5px solid #e2e8f0; border-radius: 11px;
          font-family: 'Plus Jakarta Sans', sans-serif; font-size: 14px; color: #0f172a;
          outline: none; transition: border-color .18s, box-shadow .18s, background .18s;
          box-sizing: border-box;
        }
        .crf-input::placeholder { color: #cbd5e1; }
        .crf-input:focus { border-color: #16a34a; background: #fff; box-shadow: 0 0 0 3px rgba(22,163,74,.1); }
        .crf-input--err { border-color: #ef4444 !important; background: #fff7f7 !important; box-shadow: 0 0 0 3px rgba(239,68,68,.08) !important; }
        .crf-input--disabled { background: #f1f5f9; color: #94a3b8; cursor: not-allowed; }
        .crf-err { display: flex; align-items: center; gap: 5px; font-size: 12px; color: #dc2626; font-weight: 500; margin-top: 5px; }

        

        /* Ant select */
        .crf-ant-select { width: 100% !important; font-family: 'Plus Jakarta Sans', sans-serif !important;display:table !important; }
        .crf-ant-select .ant-select-selector { height: 46px !important; border: 1.5px solid #e2e8f0 !important; border-radius: 11px !important; background: #f8fafc !important; }
        .crf-ant-select .ant-select-selection-search-input { height: 44px !important; }
        .crf-ant-select .ant-select-selection-placeholder { line-height: 44px !important; color: #cbd5e1 !important; }
        .crf-ant-select .ant-select-selection-item { line-height: 44px !important; }
        .crf-ant-select:not(.ant-select-disabled):hover .ant-select-selector { border-color: #86efac !important; }
        .crf-ant-select.ant-select-focused .ant-select-selector { border-color: #16a34a !important; box-shadow: 0 0 0 3px rgba(22,163,74,.1) !important; }

        /* KYC card */
        .crf-kyc-card { background: #fafbfc; border: 1.5px solid #e2e8f0; border-radius: 14px; padding: 20px; margin-bottom: 16px; }
        .crf-kyc-card-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
        .crf-kyc-badge { display: inline-flex; align-items: center; gap: 5px; font-size: 12px; font-weight: 700; padding: 4px 10px; border-radius: 6px; background: linear-gradient(135deg,#fffbeb,#fef3c7); border: 1.5px solid #fde68a; color: #92400e; }
        .crf-kyc-remove { font-size: 12px; font-weight: 600; color: #dc2626; background: none; border: none; cursor: pointer; padding: 4px 8px; border-radius: 6px; transition: background .15s; }
        .crf-kyc-remove:hover { background: #fee2e2; }

        /* Dropzone */
        .crf-file-hidden { display: none; }
        .crf-dropzone {
          height: 130px; width: 100%; border-radius: 12px;
          border: 2px dashed #e2e8f0; background: #f8fafc;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer; transition: border-color .18s, background .18s;
          overflow: hidden; margin-top: 2px;
        }
        .crf-dropzone:hover { border-color: #86efac; background: #f0fdf4; }
        .crf-dropzone--err { border-color: #fca5a5; background: #fff7f7; }
        .crf-dropzone-empty { display: flex; flex-direction: column; align-items: center; gap: 6px; }
        .crf-dropzone-icon { width: 44px; height: 44px; border-radius: 11px; background: #f1f5f9; border: 1.5px solid #e2e8f0; display: flex; align-items: center; justify-content: center; color: #94a3b8; }
        .crf-dropzone-title { font-size: 13px; font-weight: 700; color: #374151; }
        .crf-dropzone-sub { font-size: 12px; color: #94a3b8; font-weight: 500; }
        .crf-dropzone-preview-img { max-height: 100%; object-fit: contain; border-radius: 8px; }
        .crf-dropzone-pdf { display: flex; flex-direction: column; align-items: center; gap: 6px; }
        .crf-dropzone-pdf-icon { width: 44px; height: 44px; border-radius: 11px; background: #fff1f2; border: 1.5px solid #fecdd3; display: flex; align-items: center; justify-content: center; color: #e11d48; }
        .crf-dropzone-pdf-name { font-size: 12.5px; font-weight: 700; color: #374151; max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .crf-dropzone-pdf-sub { font-size: 11.5px; color: #94a3b8; font-weight: 500; }

        /* Payment method type selector */
        .crf-pm-type-row { display: flex; gap: 10px; margin-bottom: 20px; }
        .crf-pm-type-btn {
          display: flex; align-items: center; gap: 8px;
          padding: 10px 20px; border-radius: 10px; cursor: pointer;
          border: 1.5px solid #e2e8f0; background: #f8fafc;
          font-size: 13.5px; font-weight: 600; color: #64748b;
          transition: all .15s; user-select: none;
        }
        .crf-pm-type-btn--active { background: linear-gradient(135deg,#f0fdf4,#dcfce7); border-color: #86efac; color: #15803d; }
        .crf-pm-radio { display: none; }
        .crf-pm-type-label { pointer-events: none; }

        /* PM card */
        .crf-pm-card { background: #fafbfc; border: 1.5px solid #e2e8f0; border-radius: 14px; padding: 20px; margin-bottom: 16px; }
        .crf-pm-card-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
        .crf-pm-card-badge { display: inline-flex; align-items: center; gap: 5px; font-size: 12px; font-weight: 700; padding: 4px 10px; border-radius: 6px; background: linear-gradient(135deg,#eff6ff,#dbeafe); border: 1.5px solid #bfdbfe; color: #1d4ed8; }

        /* Add more button */
        .crf-add-more-btn {
          display: inline-flex; align-items: center; gap: 7px;
          font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13px; font-weight: 700; color: #16a34a;
          padding: 9px 16px; border-radius: 10px;
          border: 1.5px solid #bbf7d0; background: linear-gradient(135deg,#f0fdf4,#dcfce7);
          cursor: pointer; margin-top: 4px; transition: all .15s;
        }
        .crf-add-more-btn:hover { background: #dcfce7; border-color: #86efac; }

        /* Footer */
        .crf-footer {
          padding: 14px 26px;
          border-top: 1.5px solid #f1f5f9;
          background: #fafbfc;
          display: flex; align-items: center; justify-content: space-between;
          flex-wrap: wrap; gap: 10px; flex-shrink: 0;
        }
        .crf-footer-left { display: flex; align-items: center; gap: 16px; }
        .crf-dot-row { display: flex; gap: 6px; align-items: center; }
        .crf-dot { width: 7px; height: 7px; border-radius: 50%; background: #e2e8f0; transition: all .2s; }
        .crf-dot--active { width: 20px; border-radius: 4px; background: linear-gradient(90deg,#15803d,#16a34a); }
        .crf-dot--done { background: #2563eb; }
        .crf-footer-hint { display: flex; align-items: center; gap: 5px; font-size: 12px; color: #94a3b8; font-weight: 500; }
        .crf-footer-btns { display: flex; align-items: center; gap: 8px; }

        .crf-btn-back {
          display: inline-flex; align-items: center; gap: 6px;
          font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13px; font-weight: 600; color: #64748b;
          padding: 9px 16px; border-radius: 10px; border: 1.5px solid #e2e8f0; background: #fff;
          cursor: pointer; transition: all .15s;
        }
        .crf-btn-back:hover { background: #f1f5f9; border-color: #cbd5e1; color: #374151; }

        .crf-btn-cancel {
          font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13px; font-weight: 600; color: #64748b;
          padding: 9px 16px; border-radius: 10px; border: 1.5px solid #e2e8f0; background: #fff;
          cursor: pointer; transition: all .15s;
        }
        .crf-btn-cancel:hover { background: #f1f5f9; color: #374151; }

        .crf-btn-next {
          display: inline-flex; align-items: center; gap: 7px;
          font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13.5px; font-weight: 700; color: #fff;
          padding: 10px 22px; border-radius: 10px; border: none;
          background: linear-gradient(135deg, #15803d, #16a34a);
          cursor: pointer; box-shadow: 0 4px 14px rgba(22,163,74,.3);
          transition: transform .15s, box-shadow .18s;
        }
        .crf-btn-next:hover { transform: translateY(-1px); box-shadow: 0 8px 24px rgba(22,163,74,.4); }

        .crf-btn-submit {
          display: inline-flex; align-items: center; gap: 7px;
          font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13.5px; font-weight: 700; color: #fff;
          padding: 10px 22px; border-radius: 10px; border: none;
          background: linear-gradient(135deg, #1d4ed8, #2563eb);
          cursor: pointer; box-shadow: 0 4px 14px rgba(37,99,235,.3);
          transition: transform .15s, box-shadow .18s, opacity .18s;
        }
        .crf-btn-submit:hover { transform: translateY(-1px); box-shadow: 0 8px 24px rgba(37,99,235,.4); }
        .crf-btn-submit:disabled { opacity: .55; cursor: not-allowed; transform: none; }

        .crf-spinner { display: inline-block; width: 16px; height: 16px; border: 2px solid rgba(255,255,255,.35); border-top-color: #fff; border-radius: 50%; animation: crfSpin .65s linear infinite; }
        @keyframes crfSpin { to{transform:rotate(360deg)} }

        @media (max-width: 640px) {
          .crf-step-body { padding: 20px 16px 8px; }
          .crf-footer { padding: 12px 16px; }
          .crf-grid { grid-template-columns: 1fr; }
          .crf-field-full { grid-column: 1; }
          .crf-steps-row { padding: 12px 14px 10px; }
          .crf-step-label-block { display: none; }
          .crf-pm-type-row { flex-wrap: wrap; }
        }
      `}</style>
    </>
  );
}

export default CustomerRegistrationForm;